// =============================================================================
// LIGA AJUPAM — Pantalla de último recurso del ErrorBoundary raíz (v6.11.67)
// =============================================================================
// Antes esto era, literalmente:
//   <div>Ha ocurrido un error. Por favor, recarga la página.</div>
// Sin botón, sin estilo, sin decir qué pasó. Es la pantalla que vieron las
// personas de los issues LIGA-AJUPAM-51/52/53/54: la app se les había quedado
// con assets de un deploy viejo, la recuperación automática agotó sus dos
// recargas y las dejó acá, pidiéndoles que recargaran a mano en un navegador
// móvil donde el botón de recarga está escondido.
//
// Esta versión hace tres cosas que la anterior no: pone un botón, adapta el
// texto a la causa real (app actualizada / sin conexión / error genuino) y, en
// el caso de la app actualizada, la recarga purga los caches del Service Worker
// —que es justamente lo que la recarga manual del navegador NO hace, y por eso
// recargar a mano no los sacaba del pozo.
//
// Se renderiza FUERA de todos los providers (es el fallback del boundary raíz),
// así que no puede depender de contexto, de react-query ni de la API. Sólo
// markup y tokens de Tailwind.
// =============================================================================

import React from 'react';
import { AlertTriangle, RefreshCw, WifiOff } from 'lucide-react';
import { forceRecoveryReload, isOffline, isStaleChunkError } from '../utils/chunkReload';

interface AppErrorFallbackProps {
  error: unknown;
}

export const AppErrorFallback: React.FC<AppErrorFallbackProps> = ({ error }) => {
  // La causa se decide UNA vez, al montar: si la persona se queda mirando la
  // pantalla y la conexión va y viene, el texto no debe bailar.
  const [cause] = React.useState<'offline' | 'stale' | 'unknown'>(() => {
    if (isOffline()) return 'offline';
    if (isStaleChunkError(error)) return 'stale';
    return 'unknown';
  });

  const copy = {
    offline: {
      Icon: WifiOff,
      title: 'Te quedaste sin conexión',
      lead: 'No pudimos terminar de cargar la app. Fijate la señal o el WiFi y volvé a intentar.',
      cta: 'Reintentar',
    },
    stale: {
      Icon: RefreshCw,
      title: 'La app se actualizó',
      lead: 'Publicamos una versión nueva mientras la tenías abierta. Tocá el botón para tomarla — es un segundo.',
      cta: 'Cargar la versión nueva',
    },
    unknown: {
      Icon: AlertTriangle,
      title: 'Se nos rompió algo',
      lead: 'Tuvimos un error inesperado. Recargar suele alcanzar; si vuelve a pasar, avisanos.',
      cta: 'Recargar',
    },
  }[cause];

  const { Icon } = copy;

  return (
    <div className="min-h-screen bg-bg flex items-center justify-center p-6">
      <div className="w-full max-w-sm bg-surface rounded-2xl shadow-sm p-6 text-center">
        <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-bg flex items-center justify-center">
          <Icon size={22} className="text-primary" />
        </div>
        <h1 className="font-bold text-lg text-text-main mb-2">{copy.title}</h1>
        <p className="text-sm text-text-muted mb-6">{copy.lead}</p>
        <button
          onClick={forceRecoveryReload}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-primary text-text-inverted font-bold text-sm active:scale-95 transition-transform"
        >
          <RefreshCw size={16} />
          {copy.cta}
        </button>
      </div>
    </div>
  );
};
